import { apiClient } from '../lib/apiClient';

/** Outcome of the latest Fauna Marin sync, as stored by the backend. */
export interface IntegrationLastSync {
  at: string;
  status: 'ok' | 'partial' | 'failed';
  imported: number;
  updated: number;
  skipped: number;
  failed: number;
  code?: string;
}

/**
 * Response shape returned by GET /api/v1/user/integration-credentials/:provider.
 * NEVER contains the API token, ciphertext, iv, authTag or keyVersion — only
 * a saved flag, a fixed mask and metadata.
 */
export interface IntegrationCredentialsStatus {
  saved: boolean;
  /** False until ReefGuide's Fauna Marin partner key is configured on the server. */
  partnerKeyConfigured: boolean;
  credentialType?: 'api_token';
  /** Fixed mask ("••••"), never derived from the token. */
  tokenMask?: string;
  updatedAt?: string;
  /** Fauna Marin aquarium id last used for a sync (not secret), if any. */
  externalAquariumId?: string;
  lastSync?: IntegrationLastSync;
  /** True once, right after legacy email/password credentials were deleted. */
  legacyCredentialsRemoved?: boolean;
}

/** POST …/:provider/verify → the connected Fauna Marin account and its aquariums. */
export interface IntegrationConnection {
  account: { displayName: string };
  aquariums: Array<{ id: string; name: string; volume: number | null }>;
  externalAquariumId: string | null;
}

/**
 * Allow-listed integration providers supported by the backend.
 * Must match the server-side allow-list exactly: `ALLOWED_PROVIDERS` in
 * src/services/userIntegrationCredentials.service.ts is `['fauna-marin']`,
 * the same slug used by the ICP provider table and the sync route.
 */
export type IntegrationProvider = 'fauna-marin';

export const FAUNA_MARIN_PROVIDER: IntegrationProvider = 'fauna-marin';

/** Length of a Fauna Marin Lab API access token. */
export const FAUNA_MARIN_TOKEN_LENGTH = 64;

/** Where users create, renew or discard their Fauna Marin API token. */
export const FAUNA_MARIN_TOKEN_URL = 'https://lab.faunamarin.de/de/home/token';

export type TokenValidation = 'ok' | 'empty' | 'length' | 'characters';

/** Client-side check mirroring the backend (64 printable ASCII characters, no spaces). */
export function validateFaunaMarinToken(raw: string): TokenValidation {
  const value = raw.trim();
  if (value.length === 0) return 'empty';
  if (!/^[\x21-\x7E]*$/.test(value)) return 'characters';
  if (value.length !== FAUNA_MARIN_TOKEN_LENGTH) return 'length';
  return 'ok';
}

/**
 * Fetches the saved-credentials status for a given provider.
 * Returns the `{ saved: false }` shape when no token is stored — never throws
 * for the "not saved" case, only for network/auth failures.
 */
export async function getIntegrationCredentialsStatus(
  provider: IntegrationProvider,
): Promise<IntegrationCredentialsStatus> {
  // apiClient.get<T> resolves with the parsed JSON body itself (no `.data` wrapper).
  return apiClient.get<IntegrationCredentialsStatus>(
    `/api/v1/user/integration-credentials/${provider}`,
  );
}

/**
 * Saves (upserts) the user's API token. Resolves with no value on success
 * (backend returns 204). The token is never logged or cached by this module.
 */
export async function saveIntegrationToken(
  provider: IntegrationProvider,
  token: string,
): Promise<void> {
  await apiClient.put<void>(`/api/v1/user/integration-credentials/${provider}`, { token: token.trim() });
}

/** Validates the saved token with Fauna Marin and lists the user's aquariums. */
export async function verifyIntegrationConnection(
  provider: IntegrationProvider,
): Promise<IntegrationConnection> {
  return apiClient.post<IntegrationConnection>(`/api/v1/user/integration-credentials/${provider}/verify`, {});
}

/** Saves (or clears with null) the Fauna Marin aquarium mapped to ReefGuide. */
export async function saveIntegrationSettings(
  provider: IntegrationProvider,
  externalAquariumId: string | null,
): Promise<void> {
  await apiClient.put<void>(`/api/v1/user/integration-credentials/${provider}/settings`, { externalAquariumId });
}

/**
 * Deletes the saved token for a given provider. Idempotent — resolves
 * successfully whether or not a row previously existed (backend returns 204).
 */
export async function deleteIntegrationCredentials(
  provider: IntegrationProvider,
): Promise<void> {
  await apiClient.delete<void>(`/api/v1/user/integration-credentials/${provider}`);
}
